/**
 * Multi-Text-Pic Slider Component
 * Handles multi-slide slider with thumbnails, navigation, autoplay, and accessibility features
 */
;(function () {
	'use strict'

	/**
	 * Initialize all multi-text-pic sliders when DOM is ready
	 */
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initializeAllSliders)
	} else {
		initializeAllSliders()
	}

	/**
	 * Find and initialize all multi-text-pic sliders on the page
	 */
	function initializeAllSliders() {
		const sliders = document.querySelectorAll('[data-slider-id^="multi-text-pic-"]')
		sliders.forEach(sliderElement => {
			const sliderId = sliderElement.getAttribute('data-slider-id')
			if (sliderId) {
				initSlider(sliderId, sliderElement)
			}
		})
	}

	/**
	 * Initialize a single multi-text-pic slider
	 * @param {string} sliderId - Unique identifier for the slider
	 * @param {HTMLElement} sliderElement - The slider container element
	 */
	function initSlider(sliderId, sliderElement) {
		if (!sliderElement) return

		const slides = sliderElement.querySelectorAll('[role="tabpanel"]')
		const totalSlides = slides.length

		if (totalSlides === 0) return

		let currentSlide = 0
		let autoplayInterval = null
		const autoplayDelay = 5000

		// Get slider elements
		const prevBtn = sliderElement.querySelector('[onclick*="prev"]')
		const nextBtn = sliderElement.querySelector('[onclick*="next"]')
		const dotsContainer = sliderElement.querySelector('[data-slider-dots]')
		const thumbButtons = sliderElement.querySelectorAll('[onclick*="goToSlide"]')

		// Create pagination dots if container exists and we have multiple slides
		if (dotsContainer && totalSlides > 1) {
			for (let i = 0; i < totalSlides; i++) {
				const dot = document.createElement('button')
				dot.type = 'button'
				dot.role = 'tab'
				dot.className = `h-3 w-3 rounded-full transition-all focus:outline-none ${i === 0 ? 'bg-blue-600' : 'bg-gray-300'}`
				dot.setAttribute('aria-label', `Go to slide ${i + 1}`)
				dot.setAttribute('aria-selected', i === 0 ? 'true' : 'false')
				dot.tabIndex = i === 0 ? 0 : -1
				dotsContainer.appendChild(dot)
			}
		}

		/**
		 * Updates slider display and navigation elements for specified slide
		 * @param {number} index - Target slide index
		 */
		function updateSlider(index) {
			if (index < 0 || index >= totalSlides) return

			currentSlide = index

			// Update slide visibility
			slides.forEach((slide, i) => {
				if (i === index) {
					slide.style.display = 'block'
					slide.setAttribute('aria-hidden', 'false')
					slide.setAttribute('tabindex', '0')
				} else {
					slide.style.display = 'none'
					slide.setAttribute('aria-hidden', 'true')
					slide.setAttribute('tabindex', '-1')
				}
			})

			// Update pagination dots
			const dots = dotsContainer?.querySelectorAll('button')
			dots?.forEach((dot, i) => {
				if (i === index) {
					dot.classList.remove('bg-gray-300')
					dot.classList.add('bg-blue-600')
					dot.setAttribute('aria-selected', 'true')
					dot.tabIndex = 0
				} else {
					dot.classList.remove('bg-blue-600')
					dot.classList.add('bg-gray-300')
					dot.setAttribute('aria-selected', 'false')
					dot.tabIndex = -1
				}
			})

			// Update thumbnail buttons
			thumbButtons.forEach((thumb, i) => {
				if (i === index) {
					thumb.classList.remove('opacity-60')
					thumb.classList.add('opacity-100', 'ring-2', 'ring-blue-600')
					thumb.setAttribute('aria-selected', 'true')
					thumb.tabIndex = 0
				} else {
					thumb.classList.remove('opacity-100', 'ring-2', 'ring-blue-600')
					thumb.classList.add('opacity-60')
					thumb.setAttribute('aria-selected', 'false')
					thumb.tabIndex = -1
				}
			})

			// Update navigation button states
			if (prevBtn) {
				prevBtn.disabled = currentSlide === 0 && totalSlides > 1
			}
			if (nextBtn) {
				nextBtn.disabled = currentSlide === totalSlides - 1 && totalSlides > 1
			}
		}

		/**
		 * Navigate to previous or next slide
		 * @param {string} direction - 'prev' or 'next'
		 */
		function navigateSlider(direction) {
			let newIndex = currentSlide

			if (direction === 'prev') {
				newIndex = currentSlide > 0 ? currentSlide - 1 : totalSlides - 1
			} else if (direction === 'next') {
				newIndex = currentSlide < totalSlides - 1 ? currentSlide + 1 : 0
			}

			updateSlider(newIndex)
			resetAutoplay()
		}

		/**
		 * Navigate to specific slide by index
		 * @param {number} index - Target slide index
		 */
		function goToSlide(index) {
			updateSlider(index)
			resetAutoplay()
		}

		/**
		 * Start automatic slide progression
		 */
		function startAutoplay() {
			if (totalSlides <= 1) return

			stopAutoplay()
			autoplayInterval = setInterval(() => {
				navigateSlider('next')
			}, autoplayDelay)
		}

		/**
		 * Stop automatic slide progression
		 */
		function stopAutoplay() {
			if (autoplayInterval) {
				clearInterval(autoplayInterval)
				autoplayInterval = null
			}
		}

		/**
		 * Reset autoplay timer
		 */
		function resetAutoplay() {
			stopAutoplay()
			startAutoplay()
		}

		// Event Listeners Setup

		// Navigation buttons
		if (prevBtn) {
			prevBtn.removeAttribute('onclick')
			prevBtn.addEventListener('click', () => navigateSlider('prev'))
		}

		if (nextBtn) {
			nextBtn.removeAttribute('onclick')
			nextBtn.addEventListener('click', () => navigateSlider('next'))
		}

		// Pagination dots
		const dots = dotsContainer?.querySelectorAll('button')
		dots?.forEach((dot, i) => {
			dot.addEventListener('click', () => goToSlide(i))
		})

		// Thumbnail buttons
		thumbButtons.forEach((thumb, i) => {
			thumb.removeAttribute('onclick')
			thumb.addEventListener('click', () => goToSlide(i))
		})

		// Keyboard navigation
		sliderElement.addEventListener('keydown', function (e) {
			switch (e.key) {
				case 'ArrowLeft':
					e.preventDefault()
					navigateSlider('prev')
					break
				case 'ArrowRight':
					e.preventDefault()
					navigateSlider('next')
					break
				case 'Home':
					e.preventDefault()
					goToSlide(0)
					break
				case 'End':
					e.preventDefault()
					goToSlide(totalSlides - 1)
					break
			}
		})

		// Autoplay control on hover/focus
		sliderElement.addEventListener('mouseenter', stopAutoplay)
		sliderElement.addEventListener('mouseleave', startAutoplay)
		sliderElement.addEventListener('focusin', stopAutoplay)
		sliderElement.addEventListener('focusout', e => {
			if (!sliderElement.contains(e.relatedTarget)) {
				startAutoplay()
			}
		})

		// Touch/swipe support
		let touchStartX = 0
		let touchEndX = 0

		sliderElement.addEventListener(
			'touchstart',
			e => {
				touchStartX = e.changedTouches[0].screenX
			},
			{ passive: true }
		)

		sliderElement.addEventListener(
			'touchend',
			e => {
				touchEndX = e.changedTouches[0].screenX
				const diff = touchStartX - touchEndX

				if (Math.abs(diff) > 50) {
					if (diff > 0) {
						navigateSlider('next')
					} else {
						navigateSlider('prev')
					}
				}
			},
			{ passive: true }
		)

		// Initialize slider
		updateSlider(0)

		// Start autoplay if we have multiple slides
		if (totalSlides > 1) {
			startAutoplay()
		}

		// Store functions on window for backward compatibility
		window[`navigateSlider_${sliderId}`] = navigateSlider
		window[`goToSlide_${sliderId}`] = goToSlide
	}

	// Global functions for backward compatibility with inline onclick attributes
	window.navigateSlider = function (sliderId, direction) {
		const fn = window[`navigateSlider_${sliderId}`]
		if (fn) fn(direction)
	}

	window.goToSlide = function (sliderId, index) {
		const fn = window[`goToSlide_${sliderId}`]
		if (fn) fn(index)
	}

	window.handleSliderKeydown = function (event, sliderId) {
		// Keyboard handling is now done via event listeners on slider elements
		// This function is kept for backward compatibility but does nothing
	}
})()
